import React, {FC, useState} from 'react'
import {StyleSheet, TouchableOpacity} from 'react-native'
import {useTranslation} from 'react-i18next'

import {ColorVariants, NamedColors} from 'src/designSystem/colors'
import OverlaySimple, {
  OverlaySimpleProps,
} from 'src/designSystem/components/organisms/Overlay/variants/OverlaySimple'
import {
  PFStatusPill,
  PFStatusPillProps,
} from 'src/designSystem/components/atoms/PFStatusPill/PFStatusPill'
import {SvgIcon} from 'src/designSystem/components/atoms/SvgIcon/SvgIcon'
import Box from 'src/designSystem/components/atoms/Box/Box'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'

type ActionRowContentProps = {
  titleStatusPill?: PFStatusPillProps
  titleColor?: ColorVariants
  title: string
  subTitle?: string
  subTitleColor?: ColorVariants
}

const ActionRowContent = (props: ActionRowContentProps): JSX.Element => {
  const {titleStatusPill, titleColor, title, subTitle, subTitleColor} = props

  return (
    <Box gap={'tiny'} grow shrink={1}>
      {titleStatusPill ? <PFStatusPill {...titleStatusPill} /> : null}
      <PFText variant={'p_semibold'} color={titleColor ?? 'textPrimary'}>
        {title}
      </PFText>
      {subTitle ? (
        <PFText variant={'p_sm'} color={subTitleColor ?? 'textPrimary'}>
          {subTitle}
        </PFText>
      ) : null}
    </Box>
  )
}

type ActionRowInfoModal = Pick<OverlaySimpleProps, 'title' | 'content' | 'text' | 'onOkay'> & {
  onPressShowInfoModal?: () => void
}

export type ActionRowProps = ActionRowContentProps & {
  actionRowInfoModal?: ActionRowInfoModal
  infoModalTestID?: string
  onPress?: () => void
  testID?: string
  isDisabled?: boolean
}

const ActionRow: FC<ActionRowProps> = (props) => {
  const {
    actionRowInfoModal,
    infoModalTestID,
    isDisabled = false,
    onPress,
    subTitle,
    subTitleColor,
    testID,
    title,
    titleColor,
    titleStatusPill,
  } = props

  const {t} = useTranslation('Common')

  const [showInfoModal, setShowInfoModal] = useState(false)

  const actionRowContentProps: ActionRowContentProps = {
    title,
    titleColor,
    titleStatusPill,
    subTitle,
    subTitleColor,
  }

  const handleOnPress = (): void => {
    if (actionRowInfoModal) {
      actionRowInfoModal.onPressShowInfoModal?.()
      setShowInfoModal(true)
    } else if (onPress) {
      onPress()
    }
  }

  return (
    //  Render borders outside of `TouchableOpacity` so they don't change opacity unevenly `onPress`
    <Box
      boxStyle={styles.wrapper}
      paddingVertical={'small'}
      paddingHorizontal={'medium'}
      testID={testID ?? 'Action-Row'}
    >
      {actionRowInfoModal || onPress ? (
        <>
          <TouchableOpacity
            onPressIn={handleOnPress}
            testID={`${testID}-OnPress`}
            disabled={isDisabled}
          >
            <Box direction={'row'} align={'center'} gap={'medium'}>
              <ActionRowContent {...actionRowContentProps} />
              <SvgIcon
                name={actionRowInfoModal ? 'info' : 'chevronRight'}
                colorVariant={isDisabled ? 'inactive' : 'active'}
                size={'medium'}
              />
            </Box>
          </TouchableOpacity>
          {actionRowInfoModal ? (
            <OverlaySimple
              testID={infoModalTestID ?? 'Action-Row-Info-Modal'}
              visible={showInfoModal}
              okayButtonText={t('OK')}
              title={actionRowInfoModal.title}
              content={actionRowInfoModal.content}
              text={actionRowInfoModal.text}
              onOkay={(): void => {
                void actionRowInfoModal.onOkay?.()
                setShowInfoModal(false)
              }}
            />
          ) : null}
        </>
      ) : (
        <ActionRowContent {...actionRowContentProps} />
      )}
    </Box>
  )
}

export {ActionRow}

const styles = StyleSheet.create({
  wrapper: {
    borderBottomWidth: 1,
    borderColor: NamedColors.ASH,
  },
})
